import React, { useState, useMemo } from 'react';
import Modal from '../components/Modal';
import IdentityCard from '../components/IdentityCard';

const publications = [
    // 2026 Publications
    {
      title: "Three Modalities, Two Design Probes, One Prototype, and No Vision: Experience-Based Co-Design of a Multi-modal 3D Data Visualization Tool",
      authors: "Kamath S. S., Zeidieh A. N., Potluri V., O'Modhrain S., Perry K., Seo J.",
      venue: "ACM CHI conference on Human Factors in Computing Systems, 2026",
      tags: ["Accessibility", "Data Visualization", "3D Interaction", "Multimodal", "Co-Design"],
      description: "As first author, I led the end-to-end research, design, and technical development workflow for this CHI 2026 paper, positioning it as a core component of my dissertation trajectory in accessible multimodal data visualization. I developed the methodological framing for an experience-based co-design approach, defined the study structure around three interaction modalities and two design probes, and established the evaluation logic used to iteratively refine our design space. I independently designed and built both low-fidelity and high-fidelity prototypes, progressing from early concept exploration artifacts to an integrated multimodal 3D data-visualization prototype capable of supporting non-visual and cross-modal exploration workflows for blind and low-vision users. I coordinated and ran the primary data collection workflow, documented co-designer interactions across sessions, and synthesized findings through structured comparative analysis to identify convergent and divergent patterns in user strategies, interpretive psychological behaviors, and modality preferences. I translated these empirical findings into 3 concrete design requirements, prioritized implementation trade-offs, and authored the final system rationale that connected interaction-level observations to broader accessibility design principles. I also led the interpretation and consolidation of study outcomes into a coherent contribution narrative, articulating both the theoretical implications for multimodal accessible visualization research and the practical implications for future tool builders. In manuscript development, I wrote and integrated the major sections, justified methodological and design decisions with evidence from the study, and shaped the final argumentation that positioned our work within the CHI literature on co-design, non-visual interaction, and inclusive visualization systems.",
      link: "https://doi.org/10.1145/3772318.3791272",
      status: "Accepted",
      type: "Conference"
    },
    {
      title: "Managing physical activity through voice: a usability study of the PCHA app with blind and low-vision individuals",
      authors: "Choi S., Seo J., Krishnan A., Kamath S., Hernandez M. E., Kitsiou S.",
      venue: "Disability and Rehabilitation: Assistive Technology, May 2026",
      tags: ["Accessibility", "Conversational AI", "Health Technology", "Usability"],
      description: "I was responsible for redesigning the user interface to enhance intuitiveness and accessibility for blind and low-vision users. I contributed to creating the underlying logic for timing-based physical activity tracking and developed the alert system architecture specifically tailored for BLV individuals. My work encompassed designing how information could be effectively recorded and retrieved through accessible interaction patterns. Additionally, I facilitated design thinking sessions to ensure user-centered development and contributed to the theoretical framework for accessible conversational health interfaces.",
      link: "https://doi.org/10.1080/17483107.2026.2663134",
      status: "Published",
      type: "Journal"
    },

    // 2025 Publications
    {
      title: "Explore, Listen, Inspect: Supporting Multimodal Interaction with 3D Surface and Point Data Visualizations",
      authors: "Kamath S., Zeidieh A., Seo J.",
      venue: "The 27th International ACM SIGACCESS Conference on Computers and Accessibility, October 2025",
      tags: ["Data Visualization", "3D Interaction", "Multimodal", "Co-Design"],
      description: "As the lead developer of the DIXTRAL system, I was responsible for the complete technical architecture and implementation of this innovative multimodal interaction framework for 3D surface and point data visualizations. I designed and developed the core system architecture that seamlessly integrates spatial audio and text-based interaction modalities to create an accessible exploration experience for blind and low-vision users. My technical contributions included implementing sophisticated 3D spatial mapping algorithms, developing real-time audio rendering systems for complex geometric data. I established and maintained collaborative partnerships with two blind and low-vision co-designers throughout the entire development process, ensuring their direct input shaped both the technical specifications and user experience design. I was responsible for comprehensive system documentation, including technical specifications, user interaction protocols, and accessibility guidelines. Additionally, I led the manuscript writing process, articulating our novel contributions to accessible data visualization theory and practice, while systematically documenting the co-design methodology and its impact on system development. My work advanced the theoretical understanding of multimodal interaction in accessible data exploration while providing practical frameworks for developing inclusive visualization tools that empower blind and low-vision users to independently explore complex 3D datasets.",
      link: "https://doi.org/10.1145/3663547.3759765",
      status: "Published",
      type: "Demo"
    },

    {
      title: "PunchPulse: A Physically Demanding Virtual Reality Boxing Game Designed with, for and by Blind and Low-Vision Players",
      authors: "Kamath S., Khan O., Chaudhary A., Meyerhoff-Liang J., Choi S., Seo J.",
      venue: "The 27th International ACM SIGACCESS Conference on Computers and Accessibility, October 2025",
      tags: ["Accessibility", "Virtual Reality", "Co-Design", "Gaming"],
      description: "As the project lead, I conceptualized, designed, and executed this research that fundamentally reimagines accessible virtual reality gaming through participatory design methodologies. I independently developed the comprehensive research protocol, incorporating rigorous mixed-methods approaches that seamlessly integrated quantitative biomechanical analysis with qualitative phenomenological inquiry. My leadership encompassed the entire research lifecycle: from initial theoretical framework development through final manuscript preparation and submission. I authored the complete publication manuscript, systematically articulating our novel contributions to accessible VR gaming theory and practice. I conceptually designed and algorithmically specified the core game mechanics, including the innovative spatial audio mapping systems, haptic feedback protocols, and accessible user interface architectures that enable blind and low-vision players to engage in physically demanding virtual boxing experiences. My analytical contributions included developing sophisticated statistical models for performance metrics analysis, conducting comprehensive thematic coding of participant interviews using grounded theory approaches, and synthesizing quantitative physiological data with qualitative user experience narratives. I established and maintained collaborative partnerships with the blind and low-vision gaming community, ensuring authentic co-design processes that positioned community members as genuine research collaborators rather than passive subjects. Through this work, I advanced theoretical understanding of embodied interaction in accessible virtual environments while simultaneously creating practical frameworks for inclusive VR game development that prioritizes both physical engagement and cognitive accessibility.",
      link: "https://doi.org/10.1145/3663547.3746365",
      status: "Published",
      type: "Conference"
    },

    {
      title: "Sensing the Shape of Data: Non-Visual Exploration of Statistical Concepts in Histograms with Blind and Low-Vision Learners",
      authors: "Kamath S., Khan O., Zeidieh A. N., Seo J.",
      venue: "arXiv:2509.14452, September 2025",
      tags: ["Accessibility", "Data Visualization", "Statistics", "Education", "Blind and Low Vision"],
      description: "I played a central role in shaping and executing this research investigating how BLV individuals conceptualize statistical concepts through non-visual representations. I co-developed the experimental protocol and led the creation of comprehensive research materials, working closely with the PI to ensure methodological rigor. I spearheaded the implementation of the study protocol, conducting detailed sessions with BLV participants across three non-visual modalities (STGs, BDPs, sonification). My analytical contributions included performing sophisticated quantitative analysis of accuracy metrics, completion times, and confidence levels, while also conducting in-depth qualitative analysis of participants' gesture patterns and learning strategies. I led the manuscript development process, synthesizing complex findings into a cohesive narrative that highlighted the unique cognitive strategies employed by BLV learners in understanding statistical concepts. Additionally, I designed and implemented the data collection instruments, developed the coding framework for gesture analysis, and created detailed documentation of the experimental procedures for future replication.",
      link: "https://arxiv.org/abs/2509.14452",
      status: "Preprint",
      type: "Preprint"
    },

    {
      title: "Py maidr: Bridging Visual and Non-Visual Data Experiences Through a Unified Python Framework",
      authors: "Seo J., Venkatesh S., Pokar D., Kamath S., Ganesan K. A.",
      venue: "arXiv preprint arXiv:2509.13532, September 2025",
      tags: ["Accessibility", "Data Visualization", "Python", "Software Development", "Blind and Low Vision"],
      description: "I contributed to the documentation and presentation aspects of this innovative Python package. My primary contributions included designing comprehensive system architecture diagrams that effectively communicated the package's technical infrastructure and workflow. I assisted in the manuscript writing process, helping to articulate the package's capabilities and its significance in promoting inclusive data visualization practices. Additionally, I was responsible for ensuring consistent formatting and clear presentation of technical concepts throughout the paper.",
      link: "https://arxiv.org/abs/2509.13532",
      status: "Preprint",
      type: "Preprint"
    },
    
    {
      title: "Development of a personalized conversational health agent to enhance physical activity for blind and low-vision individuals",
      authors: "Choi S., Seo J., Krishnan A., Kamath S., Kitsiou S., Haegele J.",
      venue: "mHealth, July 2025",
      tags: ["Accessibility", "Conversational AI", "Health Technology"],
      description: "I was responsible for redesigning the user interface to enhance intuitiveness and accessibility for blind and low-vision users. I contributed to creating the underlying logic for timing-based physical activity tracking and developed the alert system architecture specifically tailored for BLV individuals. My work encompassed designing how information could be effectively recorded and retrieved through accessible interaction patterns. Additionally, I facilitated design thinking sessions to ensure user-centered development and contributed to the theoretical framework for accessible conversational health interfaces.",
      link: "https://doi.org/10.21037/mhealth-24-60",
      status: "Published",
      type: "Journal"
    },

    // 2024 Publications
    {
      title: "Playing Without Barriers: Crafting Playful and Accessible VR Table-Tennis with and for Blind and Low-Vision Individuals",
      authors: "Kamath S., Zeidieh A., Khan O., Sethi D., Seo J.",
      venue: "The 26th International ACM SIGACCESS Conference on Computers and Accessibility, October 2024",
      tags: ["Accessibility", "Virtual Reality", "Table Tennis", "Co-Design"],
      description: "I co-led the participatory design process and user experience research for this innovative accessible VR gaming project. My contributions included developing the co-design methodology with blind and low-vision collaborators, conducting comprehensive user studies to understand spatial audio and haptic feedback requirements, performing systematic analysis of accessibility barriers in VR gaming, and contributing to the design of novel interaction paradigms that prioritize inclusive gaming experiences.",
      link: "https://doi.org/10.1145/3663548.3688526",
      status: "Published",
      type: "Poster"
    },
    {
      title: "MAIDR Meets AI: Exploring Multimodal LLM-Based Data Visualization Interpretation by and with Blind and Low-Vision Users",
      authors: "Seo J., Kamath S., Zeidieh A., Venkatesh S., McCurry S.",
      venue: "The 26th International ACM SIGACCESS Conference on Computers and Accessibility, October 2024",
      tags: ["Accessibility", "Data Visualization", "AI", "Multimodal Interaction"],
      description: "I co-designed the experimental methodology and conducted extensive user studies to evaluate multimodal LLM interactions with blind and low-vision users. My contributions included developing the theoretical framework for accessible AI-human interaction, designing and implementing user experience protocols, performing comprehensive qualitative analysis of user interactions, and contributing to the algorithmic design considerations for inclusive multimodal interfaces.",
      link: "https://doi.org/10.1145/3663548.3675660",
      status: "Published",
      type: "Conference"
    },
    {
      title: "Designing Born-Accessible Courses in Data Science and Visualization: Challenges and Opportunities of a Remote Curriculum Taught by Blind Instructors to Blind Students",
      authors: "Seo J., O'Modhrain S., Xia Y., Kamath S., Lee B., Coughlan J. M.",
      venue: "EuroVis 2024 - Education Papers, The Eurographics Association",
      tags: ["Accessibility", "Educational Technology", "Data Science", "Remote Learning"],
      description: "I was responsible for comprehensive transcript coding and analysis, developing initial data visualizations that informed our understanding of accessible pedagogy, and conducting rigorous thematic analysis that was instrumental in shaping the study's preliminary findings. My analytical work provided crucial insights into the unique challenges and opportunities present in accessible data science education, contributing significantly to the theoretical framework for born-accessible curriculum design.",
      link: "https://doi.org/10.2312/eved.20241053",
      status: "Published",
      type: "Conference"
    },
    {
      title: "Science Fiction Prototyping In English as a Design Thinking Tool to Inspire the Future",
      authors: "Kamath S.",
      venue: "English Across Disciplines: Journal of Applied Linguistics and Language Teaching (2024)",
      tags: ["Design Thinking", "Science Fiction", "Prototyping", "Applied Linguistics"],
      description: "I conceptualized and developed this interdisciplinary study exploring the intersection of narrative design and technological innovation. I independently designed the theoretical framework connecting science fiction narrative techniques with design thinking methodologies, conducted comprehensive analysis of linguistic patterns in speculative design, and developed practical applications for using science fiction prototyping as an educational and ideation tool for inspiring future technological innovations.",
      link: "#",
      status: "Published",
      type: "Journal"
    },
    {
      title: "Technology as a Tool to Enhance Development of Skillset in Autistic Individuals: Specific to Female Gender",
      authors: "Rahaman S., Ashok V., Kamath S.",
      venue: "9th International Congress on Information and Communication Technology (ICICT 2024), 2024",
      tags: ["Accessibility", "Autism", "Gender Studies", "Technology"],
      description: "I independently drafted the entire manuscript and conducted all associated research and analysis. I was responsible for the comprehensive research design, including methodological framework development, direct interaction with specialized autistic educators, and systematic thematic categorization of key accessibility issues. My work included developing the innovative COACH Model (Child-centered design, Opportunity, Accessibility, Content Modulation, Hone Skills) specifically designed to enhance the educational experience for autistic individuals, with particular focus on gender-specific considerations.",
      link: "https://ouci.dntb.gov.ua/en/works/7PGPkA09/",
      status: "Published",
      type: "Conference"
    },

    // 2023 Publications
    {
      title: "Serious Games and Accessibility – an Exploratory Analysis",
      authors: "Kamath S., Sethi D.",
      venue: "11th International Conference on Serious Games and Applications for Health, 2023",
      tags: ["Accessibility", "Gaming", "Serious Games", "Health"],
      description: "I led the conceptualization and execution of this exploratory study, conducting comprehensive analysis of accessibility considerations in serious games applications for health outcomes. My contributions included developing the theoretical framework for accessible game design, performing systematic literature review and analysis, designing the research methodology, and conducting qualitative evaluation of existing serious games platforms to identify accessibility gaps and opportunities for inclusive design improvements.",
      link: "#",
      status: "Published",
      type: "Conference"
    },
    {
      title: "Contextualizing Privacy for Older Adults in Canada",
      authors: "Marmorato P., Swami R., Kamath S., Asikaer N., Molyneaux H., Munteanu C., Stobert E.",
      venue: "Proceedings of Symposium on Applications of Contextual Integrity (CI '23), ACM, USA, 2023",
      tags: ["Privacy", "Older Adults", "Contextual Integrity", "Canada"],
      description: "I conducted comprehensive literature review and systematic thematic analysis while simultaneously designing and implementing the Voice User Interface (VUI) and conversational interaction frameworks. I developed an innovative conversational agent utilizing Reinforcement Learning with Human Feedback (RLHF) methodology, specifically tailored to maintain older adults' motivation, autonomy, and self-engagement. My technical contributions provided critical empirical insights into privacy concerns and technological adoption patterns among this demographic, contributing to the theoretical understanding of contextual integrity in aging populations.",
      link: "https://privaci.info/symposium5/papers/Work-in-ProgressPaper-ContextualizingPrivacfor%20OlderAdultsinCanadPaola%20Marmorato.pdf",
      status: "Published",
      type: "Conference"
    },
    {
      title: "An Energy Audit-based Case Study on varied Organizations",
      authors: "Puthukkattu S. S., Poojary R., Kamath S.",
      venue: "2023 International Conference on Renewable and Sustainable Energy - ASET International Conferences, 2023",
      tags: ["Energy Audit", "Sustainability", "Renewable Energy", "Organizations"],
      description: "I contributed to the comprehensive data collection and analysis methodology, developing frameworks for systematic energy consumption assessment across diverse organizational structures. My analytical contributions included designing comparative assessment protocols, performing statistical analysis of energy usage patterns, and developing evidence-based recommendations for sustainable energy practices across different organizational typologies and operational contexts.",
      link: "https://doi.org/10.1109/aset56582.2023.10180612",
      status: "Published",
      type: "Conference"
    },
    {
      title: "Self-Dependency Amelioration and Dignity Revival for South-East Asian Older Adults – using Technology as a Means and Method",
      authors: "Kamath S., Rahaman S.",
      venue: "9th International Conference on Information and Communication Technologies for Ageing Well and e-Health (ICT4AWE 2023), April 2023",
      tags: ["Aging Technology", "South-East Asia", "Dignity", "Independence"],
      description: "I designed and administered comprehensive surveys to 41 older adults across South-East Asian contexts, performed detailed quantitative statistical analysis, and developed sophisticated data visualizations to communicate findings. I conceptualized, created, and systematically coded the innovative CALM Model (Collaborative environment, Active lifestyle engagement, Lifestyle modification, and Meaningful intercommunication), integrating evidence-based strategies to facilitate supportive environments that enhance older adults' autonomy, social connection, and dignified aging experiences.",
      link: "https://doi.org/10.5220/0011957900003476",
      status: "Published",
      type: "Conference"
    },

    // 2022 Publications
    {
      title: "Blockchain technology: a solution to address the challenges faced by the international travellers",
      authors: "Dubey S., Subramanian G., Shukla V., Dwivedi A., Puri K., Kamath S.",
      venue: "OPSEARCH, Springer, July 2022",
      tags: ["Blockchain", "Travel Technology", "Framework Development"],
      description: "I was responsible for conceptualizing the NEM blockchain framework architecture, identifying critical requirements through extensive stakeholder analysis, and devising innovative technological solutions tailored to address specific challenges encountered by international travelers. I independently conducted a comprehensive literature review, systematically gathering, analyzing, and synthesizing existing research on blockchain technology applications in the travel industry. Additionally, I led the manuscript development process, ensuring rigorous academic standards and coherent presentation of our findings.",
      link: "https://doi.org/10.1007/s12597-022-00597-x",
      status: "Published",
      type: "Journal"
    },
    {
      title: "Design and Testing of a Solar Powered Automated Fruit and Vegetable Sorter",
      authors: "Anand A., Jimoh A., Poojary R., Dudhe R., Kamath S.",
      venue: "VLSI, Signal Processing, Power Electronics, IoT, Communication and Embedded Systems (VSPICE), 2022",
      tags: ["Solar Power", "Automation", "Agriculture", "Embedded Systems"],
      description: "I contributed to the human-computer interaction design and user experience evaluation of the automated sorting system interface. My role encompassed developing user interaction protocols, conducting usability testing with agricultural workers, analyzing user experience data, and providing design recommendations for intuitive system operation. I also contributed to the evaluation methodology for assessing the system's effectiveness in real-world agricultural contexts.",
      link: "https://link.springer.com/chapter/10.1007/978-981-99-4444-6_16",
      status: "Published",
      type: "Conference"
    },
    {
      title: "Effectuating Communication for the Deaf and Hard-of-Hearing: An Ethnographic Review",
      authors: "Kamath S., Martin A., Poojary R.",
      venue: "The International Conference on Electrical and Computing technologies and Applications, pp. 80-83, 2022",
      tags: ["Accessibility", "Deaf Community", "Communication", "Ethnography"],
      description: "I was solely responsible for drafting the entire manuscript, conducting extensive systematic literature review, and developing the conceptual framework for cross-cultural analysis of handheld devices and assistive technologies for Deaf and Hard-of-Hearing (DHH) individuals. I systematically analyzed recent technological advancements in assistive communication, with particular focus on computer vision algorithms and deep learning applications for sign language recognition and interpretation, contributing novel insights into cultural considerations in assistive technology design.",
      link: "https://doi.org/10.1109/ICECTA57148.2022.9990196",
      status: "Published",
      type: "Conference"
    },
    {
      title: "Engagement of Senior Citizens in a Family Setting to Help Revive Dignity: A Study",
      authors: "Kamath S., Rahaman S.",
      venue: "8th International Conference on Information and Communication Technologies for Ageing Well and e-Health (ICT4AWE 2022), January 2022",
      tags: ["Aging Technology", "Family Dynamics", "Dignity", "Senior Citizens"],
      description: "I led the comprehensive study design and execution, conducting in-depth interviews and surveys with senior citizens and their families to examine technological interventions that facilitate meaningful engagement within family contexts. I developed the theoretical framework for dignity-centered design, performed systematic qualitative and quantitative analysis of family dynamics and technology adoption patterns, and created evidence-based recommendations for age-inclusive technology design that preserves and enhances senior citizens' autonomy and dignity.",
      link: "https://doi.org/10.5220/0011074800003188",
      status: "Published",
      type: "Conference"
    },

    // 2021 Publications
    {
      title: "Active and Participatory User Contribution to Inclusive Design for Net Zero Homes in UAE",
      authors: "Kamath S.",
      venue: "ZEMCH International Conference Proceedings, 2021",
      tags: ["Inclusive Design", "Net Zero", "UAE", "Participatory Design"],
      description: "I independently conducted this comprehensive study examining participatory design methodologies for sustainable housing in the UAE context. I developed the research framework for inclusive net-zero design, conducted extensive user research with diverse stakeholders including residents with varying abilities, performed systematic analysis of participatory design processes, and created evidence-based guidelines for active user contribution in sustainable and accessible housing design that addresses both environmental and social equity considerations.",
      link: "#",
      status: "Published",
      type: "Conference"
    }
  ];

const Publications: React.FC = () => {
  const [activeFilter, setActiveFilter] = useState<string>('All');
  const [selectedYear, setSelectedYear] = useState<string>('All');
  const [selectedPublication, setSelectedPublication] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handlePublicationClick = (publication: any) => {
    setSelectedPublication(publication);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedPublication(null);
  };

  // Get available years
  const availableYears = useMemo(() => {
    const years = publications.map(pub => {
      const yearMatch = pub.venue.match(/(?:,\s*(?:\w+\s+)?)(20\d{2})|(?:^.+\s+)(20\d{2})\s*$|(20\d{2})\s*$/);
      return yearMatch ? (yearMatch[1] || yearMatch[2] || yearMatch[3]) : '2024';
    });
    return ['All', ...Array.from(new Set(years)).sort((a, b) => b.localeCompare(a))];
  }, []);

  // Filter publications based on active filter and year
  const filteredPublications = useMemo(() => {
    let filtered = publications;
    
    if (activeFilter !== 'All') {
      if (activeFilter === 'Conference') {
        // Conference filter includes Conference, Demo, and Poster
        filtered = filtered.filter(pub => ['Conference', 'Demo', 'Poster'].includes(pub.type));
      } else {
        filtered = filtered.filter(pub => pub.type === activeFilter);
      }
    }
    
    if (selectedYear !== 'All') {
      filtered = filtered.filter(pub => {
        const yearMatch = pub.venue.match(/(?:,\s*(?:\w+\s+)?)(20\d{2})|(?:^.+\s+)(20\d{2})\s*$|(20\d{2})\s*$/);
        const pubYear = yearMatch ? (yearMatch[1] || yearMatch[2] || yearMatch[3]) : '2024';
        return pubYear === selectedYear;
      });
    }
    
    return filtered;
  }, [activeFilter, selectedYear]);

  return (
    <div className="App">
      <IdentityCard />
      {/* Hero Section */}
      <section className="hero-section">
        <div className="container">
          <div className="text-center animate-fade-in-up">
            <h1 className="page-title">Publications</h1>
          </div>
        </div>
      </section>

      {/* Filter Section */}
      <section className="section">
        <div className="container">
          <div className="text-center animate-fade-in-up">
            <div className="publication-filters">
              <div className="filter-buttons">
                {['All', 'Journal', 'Conference', 'Preprint'].map((filter) => (
                  <button
                    key={filter}
                    className={`filter-btn ${activeFilter === filter ? 'active' : ''}`}
                    onClick={() => setActiveFilter(filter)}
                    aria-pressed={activeFilter === filter}
                  >
                    {filter}
                  </button>
                ))}
              </div>
              <div className="year-filter">
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  className="year-dropdown"
                  aria-label="Filter by year"
                >
                  {availableYears.map((year) => (
                    <option key={year} value={year}>
                      {year === 'All' ? 'All Years' : year}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Publications Grid */}
      <section className="section">
        <div className="container">          
          <div className="grid grid-3 animate-stagger">
            {filteredPublications.map((pub, index) => (
              <div 
                key={index} 
                className="publication-card"
                onClick={() => handlePublicationClick(pub)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handlePublicationClick(pub);
                  }
                }}
                aria-label={`View details for ${pub.title}`}
              >
                <div className="publication-card-header">
                  <span className="publication-type-badge" data-type={pub.type.toLowerCase()}>
                    {pub.type}
                  </span>
                  {pub.type !== pub.status && (
                    <span className="publication-status-badge" data-status={pub.status.toLowerCase()}>
                      {pub.status}
                    </span>
                  )}
                </div>
                <h3 className="publication-card-title">{pub.title}</h3>
                <div className="publication-card-meta">
                  <p className="publication-authors">{pub.authors.split(',')[0]}...</p>
                  <p className="publication-year">{(() => {
                    // Extract year from venue string - look for year patterns after commas or at end
                    const yearMatch = pub.venue.match(/(?:,\s*(?:\w+\s+)?)(20\d{2})|(?:^.+\s+)(20\d{2})\s*$|(20\d{2})\s*$/);
                    return yearMatch ? (yearMatch[1] || yearMatch[2] || yearMatch[3]) : '2024';
                  })()}</p>
                </div>
                <div className="publication-card-tags">
                  {pub.tags.slice(0, 3).map((tag) => (
                    <span key={tag} className="tag">{tag}</span>
                  ))}
                  {pub.tags.length > 3 && (
                    <span className="tag-more">+{pub.tags.length - 3}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Publication Modal */}
      {selectedPublication && (
        <Modal 
          isOpen={isModalOpen} 
          onClose={closeModal}
          title={selectedPublication.title}
          technologies={selectedPublication.tags}
        >
          <div className="publication-modal-content">
            <div className="publication-modal-header">
              <div className="publication-meta-row">
                <span className="publication-type-badge" data-type={selectedPublication.type.toLowerCase()}>
                  {selectedPublication.type}
                </span>
                {selectedPublication.type !== selectedPublication.status && (
                  <span className="publication-status-badge" data-status={selectedPublication.status.toLowerCase()}>
                    {selectedPublication.status}
                  </span>
                )}
              </div>
              <div className="publication-meta-details">
                <p><strong>Authors:</strong> {selectedPublication.authors}</p>
                <p><strong>Venue:</strong> {selectedPublication.venue}</p>
              </div>
            </div>
            
            <div className="publication-description">
              <p>{selectedPublication.description}</p>
            </div>
            
            <div className="publication-actions">
              {selectedPublication.title === "PunchPulse: A Physically Demanding Virtual Reality Boxing Game Designed with, for and by Blind and Low-Vision Players" ? (
                <>
                  <a href="https://doi.org/10.1145/3663547.3746365" target="_blank" rel="noopener noreferrer" className="btn btn-dark">
                    Paper
                  </a>
                  <a href="https://www.youtube.com/watch?v=6MskhJcl-nA" target="_blank" rel="noopener noreferrer" className="btn btn-dark">
                    Video
                  </a>
                  <a href="https://github.com/xability/punch-pulse" target="_blank" rel="noopener noreferrer" className="btn btn-secondary">
                    Code
                  </a>
                </>
              ) : selectedPublication.title === "Explore, Listen, Inspect: Supporting Multimodal Interaction with 3D Surface and Point Data Visualizations" ? (
                <>
                  <a href="https://doi.org/10.1145/3663547.3759765" target="_blank" rel="noopener noreferrer" className="btn btn-dark">
                    Paper
                  </a>
                  <a href="https://github.com/SK-143381/dixtral-assets2025-demo" target="_blank" rel="noopener noreferrer" className="btn btn-secondary">
                    Code
                  </a>
                </>
              ) : (
                <>
                  {selectedPublication.title === "Science Fiction Prototyping In English as a Design Thinking Tool to Inspire the Future" ? (
                    <a href="https://www.youtube.com/watch?v=Tq_iv85e6P0" target="_blank" rel="noopener noreferrer" className="btn btn-dark">
                      Video
                    </a>
                  ) : selectedPublication.title === "Serious Games and Accessibility – an Exploratory Analysis" ? null : selectedPublication.title === "Active and Participatory User Contribution to Inclusive Design for Net Zero Homes in UAE" ? null : (
                    selectedPublication.link !== '#' ? (
                      <a href={selectedPublication.link} target="_blank" rel="noopener noreferrer" className="btn btn-dark">
                        Paper
                      </a>
                    ) : (
                      <button className="btn btn-dark" disabled>
                        Paper
                      </button>
                    )
                  )}
                  {selectedPublication.title === "Contextualizing Privacy for Older Adults in Canada" && (
                    <a href="https://privaci.info/symposium5/papers/CONTEXTUALIZINGPRIVACYFOROLDERADULTSINCANADA-Paola%20Marmorato.pdf" target="_blank" rel="noopener noreferrer" className="btn btn-secondary">
                      Presentation
                    </a>
                  )}
                </>
              )}
              {selectedPublication.title === "MAIDR Meets AI: Exploring Multimodal LLM-Based Data Visualization Interpretation by and with Blind and Low-Vision Users" && (
                <a href="https://xabilitylab.ischool.illinois.edu/a11y_dashboard/" target="_blank" rel="noopener noreferrer" className="btn btn-dark">
                  Accessible Dashboard
                </a>
              )}
            </div>
          </div>
        </Modal>
      )}

      {/* Collaboration */}
      <section className="section">
        <div className="container">
          <div className="text-center animate-fade-in-up">
            <h2 className="section-title">Research Collaborations</h2>
            <div className="row justify-content-center">
              <div className="col-lg-8">
                <p className="body-large mb-4">
                  I believe that the most impactful research emerges from collaboration. I work closely with 
                  interdisciplinary teams, including computer scientists, psychologists, designers, and most 
                  importantly, the communities I research with.
                </p>
                <p className="body-large mb-5">
                  My participatory approach ensures that research participants are not just subjects, but active 
                  collaborators in the research process, contributing their expertise and perspectives to shape 
                  both the questions we ask and the solutions we develop.
                </p>
                <div className="flex justify-center gap-4">
                  <a href="mailto:ssk11@illinois.edu" className="btn btn-dark">
                    Discuss Collaborations
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Publications;